import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PersonalCabinet from '../Components/PersonalCabinet';
import Footer from '../Components/Footer';
import { setPageTitle } from '../Utils/brand';
import { getAuthConfig } from '../Utils/authConfig';
import { LockIcon, UserCircleIcon, RobotIcon } from '../Components/Icons';

const PersonalCabinetPage = () => {
    const { cabinetId: rawCabinetId } = useParams(); // Get the cabinet ID from the URL parameters
    // Security: sanitize cabinetId parameter
    const cabinetId = React.useMemo(() => {
        return (rawCabinetId || '').replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 100);
    }, [rawCabinetId]);
    const navigate = useNavigate();

    // Check if magic auth parameter is present in current URL, sanitized against XSS
    const initialAuthToken = React.useMemo(() => {
        const params = new URLSearchParams(window.location.search);
        const raw = params.get('auth');
        return raw ? raw.replace(/[^a-zA-Z0-9._-]/g, '').slice(0, 500) : null;
    }, []);

    // Instant zero-delay initial load from local storage
    const [user, setUser] = useState(() => {
        try {
            const stored = localStorage.getItem('telegram_user') || localStorage.getItem('telegramUser');
            const token = localStorage.getItem('token') || localStorage.getItem('adminToken');
            if (stored && token) {
                return JSON.parse(stored);
            }
        } catch (_) {}
        return null;
    });

    const [loading, setLoading] = useState(() => {
        // If magic auth link is provided, we must wait for verification
        const params = new URLSearchParams(window.location.search);
        if (params.get('auth')) return true;
        // If user is already authenticated in localStorage, render immediately without blocking screen
        try {
            const stored = localStorage.getItem('telegram_user') || localStorage.getItem('telegramUser');
            const token = localStorage.getItem('token') || localStorage.getItem('adminToken');
            if (stored && token) return false;
        } catch (_) {}
        return true;
    });

    const [accessDenied, setAccessDenied] = useState(false);
    const [refreshTrigger, setRefreshTrigger] = useState(0); // Used to trigger data refresh
    const [authConfig, setAuthConfig] = useState({ authEnabled: false, botUsername: '' });

    // Check if user is already logged in and validate cabinet access
    useEffect(() => {
        setPageTitle('Personal Cabinet');
        
        // Immediately scrub ?auth= from both React Router and window.history so it never reappears on tab switches or reloads
        if (initialAuthToken) {
            const currentParams = new URLSearchParams(window.location.search);
            if (currentParams.has('auth')) {
                currentParams.delete('auth');
                const remainingQuery = currentParams.toString();
                const cleanUrl = window.location.pathname + (remainingQuery ? `?${remainingQuery}` : '') + window.location.hash;
                window.history.replaceState({}, '', cleanUrl);
                navigate({ search: remainingQuery ? `?${remainingQuery}` : '' }, { replace: true });
            }
        }

        const validateCabinetAccess = async () => {
            try {
                // Fetch auth config using shared in-memory cache
                let config = { authEnabled: false, botUsername: '' };
                try {
                    const cfgData = await getAuthConfig();
                    if (cfgData && (cfgData.success || cfgData.authEnabled !== undefined)) {
                        config = cfgData;
                        setAuthConfig(cfgData);
                    }
                } catch (_) {}

                // 1. Magic Link authentication via ?auth=<token>
                if (initialAuthToken) {

                    try {
                        const magicRes = await fetch('/api/auth/magic-login', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ token: initialAuthToken })
                        });
                        const magicData = await magicRes.json();
                        if (magicRes.ok && magicData.success && magicData.token) {
                            localStorage.setItem('token', magicData.token);
                            localStorage.setItem('telegram_user', JSON.stringify(magicData.user));
                            localStorage.setItem('telegramUser', JSON.stringify(magicData.user));
                            if (magicData.adminToken || magicData.user?.role === 'admin') {
                                localStorage.setItem('adminToken', magicData.adminToken || magicData.token);
                            }
                            setUser(magicData.user);
                            window.dispatchEvent(new Event('domainer_auth_change'));
                            window.dispatchEvent(new Event('authChange'));
                            setLoading(false);
                            return;
                        } else {
                            const errMessage = magicData.error || 'Magic link is invalid, expired, or has already been used. Please request a new link in Telegram bot.';
                            alert(errMessage);
                        }
                    } catch (mErr) {
                        console.error('Magic login error:', mErr);
                    }
                }

                // 2. Telegram Mini App initData authentication
                if (window.Telegram?.WebApp?.initData) {
                    try {
                        const tgRes = await fetch('/api/auth/telegram-webapp', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ initData: window.Telegram.WebApp.initData })
                        });
                        const tgData = await tgRes.json();
                        if (tgRes.ok && tgData.success && tgData.token) {
                            localStorage.setItem('token', tgData.token);
                            localStorage.setItem('telegram_user', JSON.stringify(tgData.user));
                            localStorage.setItem('telegramUser', JSON.stringify(tgData.user));
                            if (tgData.adminToken || tgData.user?.role === 'admin') {
                                localStorage.setItem('adminToken', tgData.adminToken || tgData.token);
                            }
                            setUser(tgData.user);
                            window.dispatchEvent(new Event('domainer_auth_change'));
                            window.dispatchEvent(new Event('authChange'));
                            setLoading(false);
                            return;
                        }
                    } catch (tErr) {
                        console.error('Telegram WebApp auth error:', tErr);
                    }
                }

                // 3. Check existing stored user session (only if auth is enabled or user is admin)
                const storedUser = localStorage.getItem('telegram_user') || localStorage.getItem('telegramUser');
                const storedToken = localStorage.getItem('token') || localStorage.getItem('adminToken');
                const adminToken = localStorage.getItem('adminToken');

                if (storedUser && storedToken) {
                    let parsedUser = null;
                    try {
                        parsedUser = JSON.parse(storedUser);
                    } catch (_) {}

                    const isAdminUser = parsedUser?.role === 'admin' || Boolean(adminToken);

                    // If user auth is disabled on server and current user is not admin, revoke session
                    if (!config.authEnabled && !isAdminUser) {
                        localStorage.removeItem('token');
                        localStorage.removeItem('telegram_user');
                        localStorage.removeItem('telegramUser');
                        setUser(null);
                        setAccessDenied(true);
                        setLoading(false);
                        window.dispatchEvent(new Event('domainer_auth_change'));
                        window.dispatchEvent(new Event('authChange'));
                        return;
                    }

                    if (parsedUser) {
                        setUser(parsedUser);
                        setLoading(false);
                        return;
                    }
                }

                // 4. Auto-restore admin session if adminToken exists (unified session)
                if (adminToken) {
                    try {
                        const demoRes = await fetch('/api/auth/admin-demo-login', {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${adminToken}`
                            },
                            body: JSON.stringify({ adminToken })
                        });
                        const demoData = await demoRes.json();
                        if (demoRes.ok && demoData.success && demoData.token) {
                            localStorage.setItem('token', demoData.token);
                            localStorage.setItem('telegram_user', JSON.stringify(demoData.user));
                            localStorage.setItem('telegramUser', JSON.stringify(demoData.user));
                            setUser(demoData.user);
                            window.dispatchEvent(new Event('domainer_auth_change'));
                            window.dispatchEvent(new Event('authChange'));
                            setLoading(false);
                            return;
                        }
                    } catch (_) {}
                }

                // 5. Backwards compatibility secret cabinet URL (:cabinetId)
                if (cabinetId) {
                    const response = await fetch(`/api/validate-cabinet-url/${cabinetId}`);
                    const result = await response.json();
                    if (result.valid) {
                        if (storedUser) setUser(JSON.parse(storedUser));
                        setLoading(false);
                        return;
                    }
                }

                // If not logged in and auth is enabled on site, show login screen instead of hard denial
                if (config.authEnabled) {
                    setAccessDenied(false);
                } else {
                    setAccessDenied(true);
                }
            } catch (error) {
                console.error('Error validating cabinet access:', error);
                setAccessDenied(true);
            } finally {
                setLoading(false);
            }
        };
        
        validateCabinetAccess();
    }, [cabinetId]);

    // Listen for auth change events (e.g., logout from Navbar) to instantly update state
    useEffect(() => {
        const syncSession = () => {
            const storedUser = localStorage.getItem('telegram_user') || localStorage.getItem('telegramUser');
            const storedToken = localStorage.getItem('token') || localStorage.getItem('adminToken');
            if (!storedUser || !storedToken) {
                setUser(null);
            } else {
                try {
                    setUser(JSON.parse(storedUser));
                } catch (_) {
                    setUser(null);
                }
            }
        };

        window.addEventListener('domainer_auth_change', syncSession);
        window.addEventListener('authChange', syncSession);
        window.addEventListener('storage', syncSession);

        return () => {
            window.removeEventListener('domainer_auth_change', syncSession);
            window.removeEventListener('authChange', syncSession);
            window.removeEventListener('storage', syncSession);
        };
    }, []);

    // Refresh data whenever the component mounts or user changes
    useEffect(() => {
        if (user) {
            // Increment refresh trigger to force data refresh
            setRefreshTrigger(prev => prev + 1);
        }
    }, [user]);

    const handleLogin = (userData) => {
        setUser(userData);
        localStorage.setItem('telegram_user', JSON.stringify(userData));
        localStorage.setItem('telegramUser', JSON.stringify(userData));
        window.dispatchEvent(new Event('domainer_auth_change'));
        window.dispatchEvent(new Event('authChange'));
        console.log('User logged in:', userData);
    };

    const handleLogout = () => {
        setUser(null);
        localStorage.removeItem('telegram_user');
        localStorage.removeItem('telegramUser');
        localStorage.removeItem('token');
        localStorage.removeItem('adminToken');
        window.dispatchEvent(new Event('domainer_auth_change'));
        window.dispatchEvent(new Event('authChange'));
        console.log('User logged out');
        navigate('/');
    };

    if (loading) {
        return (
            <main className="pt-20 min-h-screen animate-fade-simple">
                <div className="py-20 px-6 md:px-8 lg:px-16">
                    <div className="max-w-6xl mx-auto space-y-8">
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                            <div className="space-y-3">
                                <div className="h-8 w-56 skeleton-shimmer rounded-xl" />
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full skeleton-shimmer" />
                                    <div className="h-4 w-36 skeleton-shimmer rounded" />
                                </div>
                            </div>
                            <div className="h-10 w-28 skeleton-shimmer rounded-xl" />
                        </div>
                        <div className="h-12 w-full max-w-md skeleton-shimmer rounded-2xl" />
                        <div className="rounded-3xl border border-white/[0.08] bg-neutral-900/40 p-6 space-y-4">
                            {[1, 2, 3].map(i => (
                                <div key={i} className="h-16 skeleton-shimmer rounded-2xl" />
                            ))}
                        </div>
                    </div>
                </div>
                <Footer />
            </main>
        );
    }

    // Show access denied message if cabinet URL is invalid or auth disabled
    if (accessDenied) {
        return (
            <main className="pt-20 min-h-screen">
                <div className="flex justify-center items-center py-20">
                    <div className="text-center space-y-4 max-w-md">
                        <div className="w-16 h-16 mx-auto mb-4 bg-red-500/10 text-red-400 rounded-2xl flex items-center justify-center">
                            <LockIcon className="w-8 h-8" />
                        </div>
                        <h1 className="text-2xl font-bold text-white">Access Denied</h1>
                        <p className="text-neutral-400">
                            The personal cabinet URL you're trying to access is invalid or has been disabled.
                        </p>
                        <button 
                            onClick={() => navigate('/')}
                            className="mt-4 px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors"
                        >
                            Back to Home
                        </button>
                    </div>
                </div>
                <Footer />
            </main>
        );
    }

    // Show bot login card if user is not authenticated
    if (!user) {
        const botUsername = authConfig.botUsername || process.env.REACT_APP_TELEGRAM_BOT_USERNAME || '';
        const botUrl = botUsername ? `https://t.me/${botUsername}?start=cabinet` : '#';


        return (
            <main className="pt-20 min-h-screen">
                <div className="flex justify-center items-center py-20 px-4">
                    <div className="card p-8 md:p-12 text-center space-y-6 max-w-md mx-auto">
                        <div className="w-16 h-16 bg-gradient-to-r from-primary-500/20 to-secondary-500/20 text-primary-400 rounded-2xl flex items-center justify-center mx-auto">
                            <UserCircleIcon className="w-8 h-8" />
                        </div>
                        <h1 className="text-2xl font-bold text-white">Personal Cabinet</h1>
                        <p className="text-neutral-300 text-sm leading-relaxed">
                            To manage your domains, request a secure personal access link from our Telegram bot.
                        </p>
                        <div className="pt-2 space-y-3">
                            <a
                                href={botUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-full flex items-center justify-center gap-2 py-3.5 px-6 rounded-xl bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 text-white font-semibold text-sm shadow-lg shadow-primary-500/25 transition-all"
                            >
                                <RobotIcon className="w-5 h-5" />
                                <span>Get Access Link in Telegram</span>
                            </a>
                        </div>
                        <p className="text-xs text-neutral-400 leading-relaxed">
                            The link is securely tied to your Telegram account and stays valid until you log out.
                        </p>
                    </div>
                </div>
                <Footer />
            </main>
        );
    }

    return (
        <main className="pt-20 min-h-screen">
            <PersonalCabinet 
                user={user} 
                onLogin={handleLogin} 
                onLogout={handleLogout} 
                refreshTrigger={refreshTrigger} // Pass refresh trigger to force data refresh
            />
            <Footer />
        </main>
    );
};

export default PersonalCabinetPage;